<template>
  <div>
    <PurchaseFlowTabs
      :tabs="entityInfoTabs"
      :active-index="currentSubstepIndex"
      @tab-click="handleTabClick"
      :is-review="is_review"
      :is-modal-edit="is_modal_edit"
    />
    <EntitySelection v-if="isSelectionSubstep" class="mt-5 md:mt-6" />
    <div
      :class="
        is_review && entityInfoTabs[currentSubstepIndex]?.name == 'Beneficial Owners'
          ? 'h-0'
          : 'h-4'
      "
    ></div>
    <Form
      v-if="entityInfoTabs[currentSubstepIndex]?.name !== 'Beneficial Owners'"
      :schema_array="schemaToUse"
      :state_object="purchaseFlowStore.active_entity_account"
      :record_id="purchaseFlowStore.active_entity_account?.record_id"
      :is_disabled="is_review || isSelectionSubstep"
      :variant="is_review ? 'primary' : 'secondary'"
      class="mt-5 md:mt-6"
    />
    <BeneficialOwners v-else class="mt-5 md:mt-6" />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useSchemaStore, usePurchaseFlowStore, useUIStore, useDataStore } from "@/stores";
import { Form } from "@/components";
import PurchaseFlowTabs from "./PurchaseFlowUtilities/PurchaseFlowTabs.vue";
import EntitySelection from "./PurchaseFlowUtilities/EntitySelection.vue";
import BeneficialOwners from "./PurchaseFlowUtilities/BeneficialOwners.vue";

const schemaStore = useSchemaStore();
const purchaseFlowStore = usePurchaseFlowStore();
const UIStore = useUIStore();
const dataStore = useDataStore();

const emit = defineEmits(["substep-change"]);
const props = defineProps({
  is_review: { type: Boolean, default: false },
  is_modal_edit: { type: Boolean, default: false },
});

const localSubstep = ref(1);

const entityInfoTabs = computed(() => {
  const step = purchaseFlowStore.investment_steps.find((s) => s.name === "Entity Information");
  if (!step?.sub_steps) return [];

  return step.sub_steps
    .map((name) => {
      if (name === "Entity Selection") return { name, schema_group: null };
      if (name === "Entity Information") return { name, schema_group: "Entity Information" };
      if (name === "Entity Address") return { name, schema_group: "Entity Address" };
      if (name === "Beneficial Owners") return { name, schema_group: "Beneficial Owners" };
      return { name, schema_group: null };
    })
    .filter(
      (tab) => !((props.is_review || props.is_modal_edit) && tab.name === "Entity Selection")
    );
});

const currentSubstepIndex = computed(() => {
  if (props.is_review || props.is_modal_edit) return localSubstep.value - 1;
  return purchaseFlowStore.current_substep - 1;
});
const isSelectionSubstep = computed(() => {
  return entityInfoTabs.value[currentSubstepIndex.value]?.name === "Entity Selection";
});
const hasSignedInvestmentForThisEntity = computed(() => {
  const entityId = purchaseFlowStore.active_entity_account?.record_id;
  if (!entityId) return false;
  return dataStore.investments?.some(
    (inv) => inv.entity_account_id === entityId && inv.signature_date && inv.status !== "Canceled"
  );
});

const schemaToUse = computed(() => {
  const fields = getSchemaForSubstep(currentSubstepIndex.value);
  if (!hasSignedInvestmentForThisEntity.value && fields) {
    return fields.map((f) => ({ ...f, read_only: false }));
  }
  return fields;
});

function getSchemaForSubstep(index) {
  const tab = entityInfoTabs.value[index];
  return purchaseFlowStore.entity_information.find(
    (schema) => schema.group_name === tab?.schema_group
  )?.fields;
}

function handleTabClick(index) {
  const targetSubstep = index + 1;
  const currentSubstep = localSubstep.value;

  if (props.is_review) {
    localSubstep.value = targetSubstep;
    return;
  }

  if (props.is_modal_edit) {
    if (targetSubstep <= currentSubstep) {
      localSubstep.value = targetSubstep;
      return;
    }

    const result = validateRange(currentSubstep - 1, index);
    if (!result.proceed) {
      localSubstep.value = result.failedAt + 1;
      return;
    }

    localSubstep.value = targetSubstep;
    return result;
  }

  emit("substep-change", targetSubstep);
}

function validateSubstep(index) {
  const tab = entityInfoTabs.value[index];

  // Beneficial Owners has custom validation
  if (tab?.name === "Beneficial Owners") {
    const activeEntityId = purchaseFlowStore.active_entity_account.record_id;
    const beneficialOwnerIsAttached = dataStore.beneficial_owner_contacts?.some((owner) =>
      owner.entity_ids?.includes(activeEntityId)
    );
    const ownerIsSelf = dataStore.contact.entity_ids?.includes(activeEntityId);
    if (!beneficialOwnerIsAttached && !ownerIsSelf) {
      UIStore.animateNotificationAlert({
        type: "error",
        icon: "Info",
        message: `Please attach at least one beneficial owner to the entity before proceeding.`,
      });
    }

    return {
      proceed: beneficialOwnerIsAttached || ownerIsSelf,
      error_messages:
        beneficialOwnerIsAttached || ownerIsSelf
          ? []
          : ["At least one beneficial owner must be attached to the entity."],
      tabName: tab.name,
    };
  }

  const schema = getSchemaForSubstep(index);
  if (!schema) return { proceed: true };

  const result = schemaStore.validateFields(
    purchaseFlowStore.active_entity_account,
    schema,
    purchaseFlowStore.active_entity_account.record_id,
    true
  );

  return {
    proceed: result.proceed,
    invalid_fields: result.invalid_fields,
    tabName: entityInfoTabs.value[index]?.name,
    error_messages: result.error_messages,
    field_errors: result.field_errors,
  };
}

function validateRange(fromIndex, toIndex) {
  for (let i = fromIndex; i < toIndex; i++) {
    const result = validateSubstep(i);
    if (!result.proceed) {
      return {
        proceed: result.proceed,
        failedAt: i,
        tabName: result.tabName,
        invalid_fields: result.invalid_fields,
        error_messages: result.error_messages,
        field_errors: result.field_errors,
      };
    }
  }
  return { proceed: true };
}

defineExpose({
  validate() {
    const result = validateSubstep(currentSubstepIndex.value);
    // if (!result.proceed) console.log("Validation failed:", result.invalid_fields);
    return result;
  },
  validateRange,
  afterContinue: async (substepBeforeNav) => {
    const promises = [
      purchaseFlowStore.saveEntityProgress(),
      purchaseFlowStore.saveInvestmentProgress(),
    ];

    if (substepBeforeNav === entityInfoTabs.value.length) {
      promises.push(purchaseFlowStore.saveBeneficialOwnersProgress());
    }

    await Promise.all(promises);
  },
  substepCount: entityInfoTabs.value.length,
});
</script>
